import { BootstrapDialog } from '@components/shared/BootstrapDialog';
import CloseIcon from '@mui/icons-material/Close';
import {
  alpha,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { AlertCircle, Bot, Users } from 'lucide-react';

// Limit type metadata
const LIMIT_META = {
  MAX_AI_PARTICIPANTS_REACHED: {
    label: 'AI Participants',
    labelLower: 'AI participants',
    icon: Bot,
    description: 'AI participant creation is blocked until the limit is increased.',
  },
  MAX_SEATS_REACHED: {
    label: 'Team Members',
    labelLower: 'team members',
    icon: Users,
    description: 'No more team members can be invited until the limit is increased.',
  },
};

/**
 * Modal displayed when a user attempts to exceed a workspace cap limit.
 * This is shown when the API returns a 403 error with MAX_AI_PARTICIPANTS_REACHED
 * or MAX_SEATS_REACHED error codes.
 *
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {string} limitType - Type of limit that was reached (MAX_AI_PARTICIPANTS_REACHED, MAX_SEATS_REACHED)
 * @param {number} current - Current count
 * @param {number} max - Maximum allowed
 * @param {object} accountRep - Account representative info
 */
function CapLimitReachedModal({ open, onClose, limitType, current, max, accountRep }) {
  const theme = useTheme();

  const meta = LIMIT_META[limitType] || {
    label: 'Resources',
    labelLower: 'resources',
    icon: AlertCircle,
    description: 'You have reached your limit.',
  };
  const Icon = meta.icon;

  const repEmail = accountRep?.email || 'sales@qualz.ai';
  const repName = accountRep?.name || 'Qualz Sales Team';
  const hasCalendar = !!accountRep?.calendar_link;

  return (
    <BootstrapDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size={20} color={theme.palette.warning.main} />
          </Box>
          <Typography variant="h6">{meta.label} Limit Reached</Typography>
        </Box>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <Divider />

      <DialogContent>
        {/* Main message */}
        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.05),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            mb: 3,
          }}
        >
          <Typography variant="body1" mb={1}>
            Your workspace has reached its maximum of{' '}
            <Typography component="span" fontWeight={600} color="warning.main">
              {max}
            </Typography>{' '}
            {meta.labelLower}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current usage:{' '}
            <Typography component="span" fontWeight={600}>
              {current}
            </Typography>{' '}
            / {max}
          </Typography>
        </Box>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" mb={2}>
          {meta.description}
        </Typography>

        {/* Contact info */}
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Typography variant="body2" fontWeight={500} mb={1}>
            Contact your account representative to increase your limit.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {repName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Link href={`mailto:${repEmail}`} underline="hover">
              {repEmail}
            </Link>
            {accountRep?.phone && (
              <>
                {' \u2022 '}
                <Link href={`tel:${accountRep.phone}`} underline="hover">
                  {accountRep.phone}
                </Link>
              </>
            )}
          </Typography>
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
        {hasCalendar ? (
          <Button
            variant="contained"
            component={Link}
            href={accountRep.calendar_link}
            target="_blank"
            rel="noopener noreferrer"
          >
            Schedule Meeting
          </Button>
        ) : (
          <Button
            variant="contained"
            component={Link}
            href={`mailto:${repEmail}?subject=Request%20to%20Increase%20${encodeURIComponent(meta.label)}%20Limit`}
          >
            Contact Rep
          </Button>
        )}
      </DialogActions>
    </BootstrapDialog>
  );
}

export default CapLimitReachedModal;
